import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toLocale } from "@core/locale";
import { isApiError } from "@core/errors";
import { applicationKeys } from "@modules/applications/_shared/keys";
import {
  createTeamMember,
  deleteApplicationDocument,
  deleteTeamMember,
  getApplication,
  patchFounder,
  patchPractical,
  patchProject,
  patchTeam,
  patchTeamMember,
  putAnswer,
  uploadDocument as uploadDocumentSequence,
} from "../services";
import type { Application } from "../types";

/** What the "saved" indicator shows. `offline` is honest about not reaching the server. */
export type SaveStatus = "idle" | "saving" | "saved" | "offline" | "error";

/** Result of a document upload/delete — one message, not per-field, since there's no form field to key it to. */
export type DocumentMutationResult =
  { ok: true } | { ok: false; message: string };

/** The four tabs, each with a working PATCH endpoint. */
export type SavableSection = "founder" | "project" | "team" | "practical";

/** Result of a member create/delete — the one place the queue reports back synchronously. */
export type MemberMutationResult =
  { ok: true } | { ok: false; fieldErrors: Record<string, string> };

/** One queued write. Resolved against whatever the revision is when its turn comes. */
type Job =
  | {
      kind: "section";
      section: SavableSection;
      values: Record<string, unknown>;
    }
  | { kind: "answer"; questionId: string; value: unknown }
  | {
      kind: "member-create";
      values: { name: string; role: string };
      onSettled: (result: MemberMutationResult) => void;
    }
  | { kind: "member-update"; memberId: string; values: Record<string, unknown> }
  | {
      kind: "member-delete";
      memberId: string;
      onSettled: (result: MemberMutationResult) => void;
    };

/** Which PATCH a section writes through — the one thing that actually differs between them. */
function patchSection(
  section: SavableSection,
  applicationId: string,
  revision: number,
  values: Record<string, unknown>
): Promise<Application> {
  switch (section) {
    case "founder":
      return patchFounder(applicationId, revision, values);
    case "project":
      return patchProject(applicationId, revision, values);
    case "team":
      return patchTeam(applicationId, revision, values);
    case "practical":
      return patchPractical(applicationId, revision, values);
  }
}

/**
 * The server hands back `current_revision` on a conflict, so recovery needs no refetch.
 *
 * Exported — `useDossierReview`'s consent/submit mutations hit the exact same
 * `409 stale_application_revision` shape and need the identical check; duplicating five lines
 * would just be a second copy to keep in sync if the error shape ever changes.
 */
export function conflictRevision(error: unknown): number | null {
  if (!isApiError(error)) return null;
  if (error.code !== "stale_application_revision") return null;
  // `body` is the raw parsed response — the conflict carries `current_revision` beside the code,
  // verified against the running stack: `{"code":"stale_application_revision","current_revision":2}`.
  const body = error.body as { current_revision?: unknown } | null;
  return typeof body?.current_revision === "number"
    ? body.current_revision
    : null;
}

/**
 * Which fields a rejected write named, if any.
 *
 * Verified against the running stack: a bad founder value comes back as
 * `{"code": "application_profile_invalid", "weekly_commitment_code": ["Invalid selection."]}` —
 * `code` and the field keys sit side by side in the same body, and the axios layer already splits
 * them into `error.fieldErrors` for exactly this shape (see `@core/errors`).
 */
function invalidFields(error: unknown): string[] {
  if (!isApiError(error)) return [];
  return error.fieldErrors ? Object.keys(error.fieldErrors) : [];
}

/**
 * The dossier's autosave — ONE serialized queue for the WHOLE application, shared by every step
 * (US-E3-06).
 *
 * ## Why one queue for every section, not one per step
 *
 * `expected_revision` is a property of the APPLICATION, not of a section — a founder write and a
 * project write bump the exact same counter. So a Founder-tab edit and a Project-tab edit queued a
 * moment apart still have to serialize against each other, which is only possible if both go
 * through this same hook instance. `ApplicationScreen` creates it once and hands `saveSection` down
 * to whichever step is on screen; it must never be instantiated per-step.
 *
 * ## Coalescing, so typing does not become a request per keystroke
 *
 * Consecutive edits to the SAME section merge into its pending job rather than queueing behind it.
 * A long-text answer edited for a minute is one field, and the applicant only ever needs its LAST
 * value on the server. Answers coalesce per question id for the same reason. Two DIFFERENT
 * sections queue as two separate jobs — a founder edit and a project edit never merge, since they
 * PATCH different endpoints.
 *
 * ## What the caller must NOT do
 *
 * Do not merge the response field by field. Every write returns the full DTO with recalculated
 * progress, regenerated warnings and a rebuilt missing-items list — more than was sent — so the
 * cache is replaced wholesale.
 *
 * ## Honest status (US-E3-06 step 3)
 *
 * *"An indicator that shows saved when the request never left the device is worse than no indicator
 * at all, because the candidat stops checking."* So `saved` is set only after a response is applied,
 * and a transport failure reports `offline` rather than silently retrying forever.
 */
export function useDossierAutosave(applicationId: string) {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation("acquire");
  const locale = toLocale(i18n.resolvedLanguage ?? "fr");
  const key = applicationKeys.detail(applicationId, locale);

  const [status, setStatus] = useState<SaveStatus>("idle");

  /**
   * Which fields the server rejected — a field id (or question id) to a translated, generic
   * message.
   *
   * ⚠️ Never the raw DRF text. `PATCH /founder/` and `PATCH /project/` return real English
   * sentences straight from Django's validators (`"Invalid selection."`), and this is a bilingual
   * UI — the same call the signup form already made for its own 400s. Only WHICH field failed comes
   * from the server; the words shown come from `dossier.errors.invalid-value`, in whichever
   * language the applicant is reading.
   *
   * Shared across ALL sections in one flat map. Safe because field ids and question ids are UUIDs
   * or stable snake_case keys unique dossier-wide — `weekly_commitment_code` is a founder key and
   * `client_count_code` a project key, and the two alphabets never collide.
   *
   * ⚠️ Team member fields are the one exception: `name`/`role`/etc. are NOT unique dossier-wide (a
   * second member can share a first member's rejected field name), so `member-update` jobs key
   * their errors `member:{memberId}:{field}` instead of the bare field name. `member-create` and
   * `member-delete` never enter this map at all — they report through `onSettled` so the roster's
   * own draft form can show the error without touching shared state.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Jobs waiting their turn. Mutated in place so a coalesce reaches the queued job. */
  const pending = useRef<Job[]>([]);
  const running = useRef(false);

  /**
   * Serializes document actions against EACH OTHER — not against field saves (that's the
   * `pending`/`drain` queue above; documents deliberately stay outside it, see `uploadDocument`'s
   * doc comment). Without this, clicking "Add" on two different requirements back to back races:
   * both read the same cached revision, both intents can even succeed, but whichever `complete`
   * call loses the race reads a now-stale revision and 409s — verified live, this was the second
   * half of the bug the first version of this hook shipped with (the first half was staleness
   * WITHIN one upload's own intent→complete gap, fixed via `onIntentSucceeded` below). A plain
   * promise chain is enough: each call awaits the previous one's full completion (including its
   * own retry and refetch) before starting, so it always sees a correct, fresh revision.
   */
  const documentMutex = useRef<Promise<void>>(Promise.resolve());
  const serializeDocumentAction = useCallback(
    <T>(run: () => Promise<T>): Promise<T> => {
      // `documentMutex.current` is always a settled-to-resolved chain (never rejects), so a plain
      // `.then(run)` is enough to queue behind whatever is already running.
      const result = documentMutex.current.then(run);
      documentMutex.current = result.then(
        () => undefined,
        () => undefined
      );
      return result;
    },
    []
  );

  const drain = useCallback(async () => {
    if (running.current) return;
    running.current = true;

    try {
      while (pending.current.length > 0) {
        const job = pending.current.shift();
        if (!job) break;

        /**
         * The revision is read from the CACHE at send time, never captured in a closure.
         *
         * A closure would hold whatever the revision was when the handler was created — for a field
         * whose `onChange` was registered on first render, revision 1 forever, and a conflict on
         * every save after the first.
         */
        const application = queryClient.getQueryData<Application>(key);
        if (!application) break;

        let revision = application.revision;
        setStatus("saving");

        // The keys THIS job is about to touch — used to clear their old errors on success and to
        // scope which errors a rejection can set. A section job may write several fields at once;
        // an answer job is always exactly one question. Member jobs are namespaced
        // `member:{memberId}:{field}` so two different members' identically-named fields (both
        // named "name", say) never clobber each other in the one flat map. Create/delete report
        // through `onSettled` instead — see the comment on `MemberMutationResult`.
        const touchedKeys =
          job.kind === "section"
            ? Object.keys(job.values)
            : job.kind === "answer"
              ? [job.questionId]
              : job.kind === "member-update"
                ? Object.keys(job.values).map(
                    (field) => `member:${job.memberId}:${field}`
                  )
                : [];

        // One retry, and only for a conflict: the server told us the real revision, so the same
        // write with the right number is the correct move rather than an error the applicant sees.
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const next = await (job.kind === "section"
              ? patchSection(job.section, applicationId, revision, job.values)
              : job.kind === "answer"
                ? putAnswer(applicationId, job.questionId, revision, job.value)
                : job.kind === "member-create"
                  ? createTeamMember(applicationId, revision, job.values)
                  : job.kind === "member-update"
                    ? patchTeamMember(
                        applicationId,
                        job.memberId,
                        revision,
                        job.values
                      )
                    : deleteTeamMember(applicationId, job.memberId, revision));
            queryClient.setQueryData(key, next);
            setStatus("saved");
            // The write that just landed is proof the value is valid now — drop any stale error
            // for exactly the fields it touched, leaving errors on OTHER fields (from an earlier,
            // still-unresolved rejection) alone.
            setFieldErrors((current) => {
              if (touchedKeys.every((field) => !(field in current)))
                return current;
              const updated = { ...current };
              for (const field of touchedKeys) delete updated[field];
              return updated;
            });
            if (job.kind === "member-create" || job.kind === "member-delete") {
              job.onSettled({ ok: true });
            }
            break;
          } catch (error) {
            const fresh = conflictRevision(error);
            if (fresh !== null && attempt === 0) {
              revision = fresh;
              continue;
            }
            const rejected = invalidFields(error);
            if (job.kind === "member-create" || job.kind === "member-delete") {
              // The draft form (or the row being removed) owns its own error display — a create
              // rejected for "name" must not collide with some other field named "name" in the
              // shared map, so it never enters `fieldErrors` at all.
              const errors: Record<string, string> = {};
              for (const field of rejected)
                errors[field] = t("dossier.errors.invalid-value");
              job.onSettled({ ok: false, fieldErrors: errors });
            } else if (rejected.length > 0) {
              // A hard input error belongs to the field and is rendered there — never the raw
              // Django sentence (see the field's own doc comment), just which field and a
              // translated, generic reason.
              const namespacedRejected =
                job.kind === "member-update"
                  ? rejected.map((field) => `member:${job.memberId}:${field}`)
                  : rejected;
              setFieldErrors((current) => {
                const updated = { ...current };
                for (const field of namespacedRejected) {
                  updated[field] = t("dossier.errors.invalid-value");
                }
                return updated;
              });
            }
            // A transport failure is the "offline" case US-E3-06 insists is reported honestly,
            // distinct from a value the server named as invalid. Either way the queue keeps going
            // — one rejected field must not freeze every other save on the page.
            setStatus(
              rejected.length > 0
                ? "error"
                : isApiError(error)
                  ? "error"
                  : "offline"
            );
            break;
          }
        }
      }
    } finally {
      running.current = false;
    }
  }, [applicationId, key, queryClient, t]);

  /**
   * Queue a change to one section (founder, project, …). Merges with an already-queued job for the
   * SAME section; a different section queues as its own separate job.
   *
   * Clears any stored error for the fields in THIS call before the request even goes out — the
   * applicant just supplied a new value, so the old accusation no longer applies. If the new value
   * is still wrong, the same field is set again once the rejection comes back; if it lands, the
   * `drain` loop's own clear (belt and suspenders) finds nothing left to remove.
   */
  const saveSection = useCallback(
    (section: SavableSection, values: Record<string, unknown>) => {
      const queued = pending.current.find(
        (job) => job.kind === "section" && job.section === section
      );
      if (queued && queued.kind === "section") {
        Object.assign(queued.values, values);
      } else {
        pending.current.push({
          kind: "section",
          section,
          values: { ...values },
        });
      }
      setFieldErrors((current) => {
        const touched = Object.keys(values);
        if (touched.every((field) => !(field in current))) return current;
        const updated = { ...current };
        for (const field of touched) delete updated[field];
        return updated;
      });
      void drain();
    },
    [drain]
  );

  /** Queue one authored answer. Merges with a queued job for the SAME question. Same clear-on-queue as `saveSection`. */
  const saveAnswer = useCallback(
    (questionId: string, value: unknown) => {
      const queued = pending.current.find(
        (job) => job.kind === "answer" && job.questionId === questionId
      );
      if (queued && queued.kind === "answer") {
        queued.value = value;
      } else {
        pending.current.push({ kind: "answer", questionId, value });
      }
      setFieldErrors((current) => {
        if (!(questionId in current)) return current;
        const updated = { ...current };
        delete updated[questionId];
        return updated;
      });
      void drain();
    },
    [drain]
  );

  /**
   * Add one named team member. Resolves once the request settles — unlike `saveSection`, the
   * caller (the roster's "Add member" draft form) needs to know whether it can close, so this is
   * NOT fire-and-forget.
   */
  const createMember = useCallback(
    (values: { name: string; role: string }) =>
      new Promise<MemberMutationResult>((resolve) => {
        pending.current.push({
          kind: "member-create",
          values,
          onSettled: resolve,
        });
        void drain();
      }),
    [drain]
  );

  /**
   * Edit one existing member's fields. Fire-and-forget, coalesced per member — the same
   * autosave-on-commit shape as `saveSection`, just namespaced so two members never share an
   * error slot. See `fieldErrors`' own doc comment for the `member:{id}:{field}` convention.
   */
  const updateMember = useCallback(
    (memberId: string, values: Record<string, unknown>) => {
      const queued = pending.current.find(
        (job) => job.kind === "member-update" && job.memberId === memberId
      );
      if (queued && queued.kind === "member-update") {
        Object.assign(queued.values, values);
      } else {
        pending.current.push({
          kind: "member-update",
          memberId,
          values: { ...values },
        });
      }
      setFieldErrors((current) => {
        const touched = Object.keys(values).map(
          (field) => `member:${memberId}:${field}`
        );
        if (touched.every((field) => !(field in current))) return current;
        const updated = { ...current };
        for (const field of touched) delete updated[field];
        return updated;
      });
      void drain();
    },
    [drain]
  );

  /** Remove one member. Same "the caller needs to know" reasoning as `createMember`. */
  const deleteMember = useCallback(
    (memberId: string) =>
      new Promise<MemberMutationResult>((resolve) => {
        pending.current.push({
          kind: "member-delete",
          memberId,
          onSettled: resolve,
        });
        void drain();
      }),
    [drain]
  );

  /**
   * Refetch the application and replace the cache wholesale. Documents are the one part of this
   * module where the mutation responses (`upload-intents/`, `.../complete/`, the bare `DELETE`)
   * do NOT return the full Application DTO — see each service function's own doc comment — so
   * there is nothing to write into the cache after a document action except a fresh GET.
   */
  const refetchApplication = useCallback(async () => {
    const fresh = await getApplication(applicationId, locale);
    queryClient.setQueryData(key, fresh);
    return fresh;
  }, [applicationId, locale, queryClient, key]);

  /**
   * Upload one file against one requirement, then refetch. Deliberately OUTSIDE the
   * `pending`/`drain` queue every other mutation goes through: a file transfer can run for real
   * seconds (there is no on-device compression — see `ApplicationSchema.documents`'s doc comment
   * on why), and holding the shared queue for that long would stall an unrelated field save with
   * no benefit. Revision safety instead comes from reading `queryClient.getQueryData(key)` fresh
   * for EACH of the two revision-checked calls and retrying once on `stale_application_revision`,
   * the same recovery every other mutation in this hook already does.
   */
  const uploadDocument = useCallback(
    (requirementId: string, file: File): Promise<DocumentMutationResult> =>
      serializeDocumentAction(async () => {
        const readRevision = () =>
          queryClient.getQueryData<Application>(key)?.revision ?? 0;
        // The intent call bumps the server's revision before the (possibly slow) file transfer
        // even starts. Patching the cache the moment it succeeds is what makes the SECOND
        // `readRevision()` call — right before `complete` — see that, instead of the same stale
        // value the intent call itself used.
        const onIntentSucceeded = (newRevision: number) => {
          queryClient.setQueryData<Application>(key, (current) =>
            current ? { ...current, revision: newRevision } : current
          );
        };

        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            await uploadDocumentSequence(
              applicationId,
              requirementId,
              file,
              readRevision,
              onIntentSucceeded
            );
            await refetchApplication();
            return { ok: true };
          } catch (error) {
            const fresh = conflictRevision(error);
            if (fresh !== null && attempt === 0) {
              await refetchApplication();
              continue;
            }
            return {
              ok: false,
              message: t("dossier.documents.upload-failed"),
            };
          }
        }
        return { ok: false, message: t("dossier.documents.upload-failed") };
      }),
    [
      applicationId,
      queryClient,
      key,
      refetchApplication,
      t,
      serializeDocumentAction,
    ]
  );

  /** Same shape as `uploadDocument` — serialized against other document actions via the same mutex, own retry-once-on-conflict, refetch on success. */
  const deleteDocument = useCallback(
    (documentId: string): Promise<DocumentMutationResult> =>
      serializeDocumentAction(async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const revision =
            queryClient.getQueryData<Application>(key)?.revision ?? 0;
          try {
            await deleteApplicationDocument(
              applicationId,
              documentId,
              revision
            );
            await refetchApplication();
            return { ok: true };
          } catch (error) {
            const fresh = conflictRevision(error);
            if (fresh !== null && attempt === 0) {
              await refetchApplication();
              continue;
            }
            return {
              ok: false,
              message: t("dossier.documents.delete-failed"),
            };
          }
        }
        return { ok: false, message: t("dossier.documents.delete-failed") };
      }),
    [
      applicationId,
      queryClient,
      key,
      refetchApplication,
      t,
      serializeDocumentAction,
    ]
  );

  return {
    status,
    fieldErrors,
    saveSection,
    saveAnswer,
    createMember,
    updateMember,
    deleteMember,
    uploadDocument,
    deleteDocument,
  };
}
